import { useEffect, useState } from "react";
import { Activity, Monitor, Smartphone } from "lucide-react";
import { Badge } from "@studio";
import { useActiveTasks } from "../lib/queries";
import type { ActiveTask, TaskRow } from "../lib/types";
import { ErrorBox, Panel } from "../console/ui";
import { errorMessage } from "../lib/api";

/**
 * 진행 중 작업 카드 (시안 c9ee685 active-runs.tsx, 요청서 7-2). 상황판과 머신 상세 › 상태가 같은 카드를 쓴다.
 * 누르면 실행 상세를 "실시간 로그" 탭으로 연다.
 */

export type ActiveCard = {
  key: string;
  runId: string | null;
  customerId: string;
  title: string;
  machine: string | null;
  device: string | null;
  stage: string;
  since: string | null;
  waiting: boolean;
  line: string | null;
};

const stageNames: Record<string, string> = { review: "원고 검토", run: "작업 수행", posted: "작업 완료", verify: "완료 검토", done: "최종 보고" };

export const cardFromActive = (t: ActiveTask): ActiveCard => ({
  key: t.taskId,
  runId: t.runId,
  customerId: t.customerId,
  title: `${t.title}${t.customerName ? ` · ${t.targetCustomerName ?? t.customerName}` : ""}`,
  machine: t.machineLabel,
  device: t.deviceAlias || t.deviceSerial,
  stage: t.stage ? (stageNames[t.stage] ?? t.stageText ?? t.stage) : t.status === "queued" ? "배정 대기" : "작업 수행",
  since: t.startedAt ?? t.assignedAt ?? t.queuedAt,
  waiting: t.status === "queued",
  line: t.lastEvent?.text ?? null,
});

export const cardFromTask = (t: TaskRow, customerId: string): ActiveCard => ({
  key: t.id,
  runId: t.runId,
  customerId,
  title: t.title ?? "작업",
  machine: t.machineLabel ?? null,
  device: t.assignedDeviceSerial,
  stage: t.stage ? (stageNames[t.stage] ?? t.stage) : t.status === "queued" ? "배정 대기" : "작업 수행",
  since: t.startedAt,
  waiting: t.status === "queued",
  line: null,
});

function useNow(live: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);
  return now;
}

const elapsed = (since: string | null, now: number) => {
  if (!since) return "";
  const s = Math.max(0, Math.round((now - Date.parse(since)) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")} 경과`;
};

export function ActiveRunCards({ cards, onOpen, error, onRetry }: { cards: ActiveCard[]; onOpen: (c: ActiveCard) => void; error?: string | null; onRetry?: () => void }) {
  const now = useNow(cards.length > 0);
  return (
    <Panel title="진행 중 작업" extra={<Badge tone="teal">{cards.length}건</Badge>}>
      {error ? (
        <ErrorBox error={error} onRetry={onRetry} />
      ) : (
        <div className="lwe-active-list">
          {cards.length ? (
            cards.map((c) => (
              <button key={c.key} type="button" className="lwe-active-card" onClick={() => onOpen(c)} disabled={!c.runId}>
                <div className="lwe-active-title">
                  <Activity size={16} />
                  <strong>{c.title}</strong>
                  <span className="lwe-live-dot" aria-label="진행 중" />
                </div>
                <div className="lwe-active-meta">
                  <span>
                    <Monitor size={13} />
                    {c.machine ?? "배정 전"}
                  </span>
                  <span>
                    <Smartphone size={13} />
                    {c.device ?? "디바이스 미정"}
                  </span>
                </div>
                <div className="lwe-active-meta">
                  <Badge tone="blue">{c.stage}</Badge>
                  <span>{c.waiting ? "배정 대기" : elapsed(c.since, now)} · 실시간 로그 보기 →</span>
                </div>
                {c.line && <p>{c.line}</p>}
              </button>
            ))
          ) : (
            <div className="lwe-active-empty">
              <Activity size={20} />
              <strong>진행 중인 작업이 없습니다</strong>
              <small>작업이 시작되면 이곳에서 실시간 로그를 확인할 수 있습니다.</small>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/** 상황판: 전 고객사 진행 중 작업 (GET /admin/runs/active). */
export function AdminActiveRuns({ onOpen }: { onOpen: (c: ActiveCard) => void }) {
  const q = useActiveTasks();
  return <ActiveRunCards cards={(q.data?.items ?? []).map(cardFromActive)} onOpen={onOpen} error={q.error ? errorMessage(q.error) : null} onRetry={() => void q.refetch()} />;
}
